"use client";

import { useState, useRef, useEffect } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AlertCircleIcon,
  Location01FreeIcons,
} from "@hugeicons/core-free-icons";
import { DestinationSearchModal } from "./DestinationSearchModal";
import { Typography } from "../ui/typography";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Input } from "../ui/input";
import type { City } from "@/types/city";
import { useCities } from "@/lib/react-query/queries/references/use-cities";

interface DestinationSearchProps {
  value?: string; // This will be the slug
  onChange: (value: string) => void; // This will receive the slug
  error?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  onSelectionComplete?: () => void; // Called when a selection is made and modal closes
}

export function DestinationSearch({
  value,
  onChange,
  error,
  className = "relative flex-1 border border-gray-100 md:border-none rounded-lg px-3 py-2.5 md:p-0",
  placeholder = "مقصدت کجاست؟",
  disabled = false,
  onSelectionComplete,
}: DestinationSearchProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef(search);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Get cities data from API
  const { data: citiesResponse } = useCities();

  const cities = citiesResponse?.data || [];

  searchRef.current = search;

  // Get display name from slug
  const getDisplayName = (slug: string | undefined): string => {
    if (!slug) return "";

    // Find city by slug
    const city = cities.find((c: City) => c.slug === slug);
    if (city) {
      return city.name;
    }

    // If not found in cities, it might be a province name or recent search
    return slug;
  };

  const displayName = getDisplayName(value);

  // Reset search when modal closes
  useEffect(() => {
    if (!open) {
      setSearch("");
      setIsFocused(false);
    }
  }, [open]);

  // Handle selection from modal
  const handleSelect = (selectedSlug: string) => {
    if (disabled) return;
    onChange(selectedSlug); // Send slug to parent
    setSearch("");
    setOpen(false);
    inputRef.current?.blur();
    // Notify parent that selection is complete
    onSelectionComplete?.();
  };

  // Handle input change (desktop only)
  const handleInputChange = (newValue: string) => {
    if (disabled) return;
    setSearch(newValue);

    // Open modal when typing on desktop
    if (!isMobile && newValue.length > 0 && !open) {
      setOpen(true);
    }
  };

  // Handle input focus (desktop only)
  const handleInputFocus = () => {
    if (disabled) return;
    setIsFocused(true);
    // On desktop, open modal on focus if there's search text
    if (!isMobile && searchRef.current.length > 0) {
      setOpen(true);
    }
  };

  // Handle input blur (desktop only)
  const handleInputBlur = () => {
    setIsFocused(false);
    // Don't close modal immediately to allow for selection
    setTimeout(() => {
      if (!isMobile && searchRef.current.length === 0) {
        setOpen(false);
      }
    }, 200);
  };

  // Keep Space/Enter from bubbling to PopoverTrigger (role=button),
  // which would toggle the popover closed and clear the query mid-typing.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (disabled) return;
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === "Enter" && !open) {
      setOpen(true);
    }
  };

  // Handle click to open modal
  const handleClick = () => {
    if (disabled) return;
    if (!open) {
      setOpen(true);
    }
  };

  // Keep typed text visible while the popover is open, even if focus moves
  // to the list (blur would otherwise swap back to the selected display name).
  const inputValue = isFocused || open ? search : displayName;
  const showPlaceholder = !isFocused && !open && !value;

  // Create trigger element
  const triggerElement = isMobile ? (
    <div
      className="flex items-center gap-1.5 cursor-pointer w-full"
      onClick={handleClick}
    >
      <HugeiconsIcon
        icon={Location01FreeIcons}
        size={20}
        className={`size-5 ${value ? "text-foreground" : "text-black-300"}`}
        strokeWidth={1.5}
      />
      <Typography
        variant="captionRegular"
        color={value ? "default" : "black300"}
        className={`md:text-sm ${value ? "font-medium!" : ""}`}
      >
        {displayName || placeholder}
      </Typography>
    </div>
  ) : (
    <div className={`flex items-center gap-1.5 w-full`} onClick={handleClick}>
      {error ? (
        <HugeiconsIcon
          icon={AlertCircleIcon}
          size={16}
          className={`shrink-0 text-error-500`}
          strokeWidth={1.5}
        />
      ) : (
        <HugeiconsIcon
          icon={Location01FreeIcons}
          size={20}
          className={`shrink-0 size-5 ${
            value || isFocused || open ? "text-foreground" : "text-black-300"
          }`}
          strokeWidth={1.5}
        />
      )}
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onCompositionEnd={(e) => handleInputChange(e.currentTarget.value)}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={showPlaceholder ? placeholder : ""}
        disabled={disabled}
        className={`text-foreground w-full bg-transparent disabled:opacity-100 outline-none border-none font-medium placeholder:font-normal px-0 ${
          error ? "placeholder:!text-error-500" : ""
        }`}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
      />
    </div>
  );

  return (
    <div className={className}>
      {/* Search Modal with Trigger */}
      <DestinationSearchModal
        open={open && !disabled}
        onOpenChange={(isOpen) => !disabled && setOpen(isOpen)}
        search={search}
        onSearchChange={handleInputChange}
        onSelect={handleSelect}
        isMobile={isMobile}
        trigger={triggerElement}
      />
    </div>
  );
}
